import { inject, Injectable } from '@angular/core';
import { Environment } from '@rma/generic/util/environment';
import { TempFeatureFlags } from '@rma/generic/util/feature-flags';
import { combineLatest, map, Observable } from 'rxjs';
import { COLLECT_GROUP_FEATURES, CollectGroupFeature, getCollectGroupItems } from '../entities/nav-items-collect';
import {
  getManageGroupItems,
  getSettingsGroupItems,
  MANAGE_GROUP_FEATURES,
  ManageGroupFeature,
  SettingGroupFeature,
  SETTINGS_GROUP_FEATURES,
} from '../entities/nav-items-manage';
import { getOverview, getProfile, NavigationUserType } from '../entities/nav-items-misc';
import { getMonitorGroupItems, MonitorGroupFeature, MONITORING_GROUP_FEATURES } from '../entities/nav-items-monitor';
import { filterShareItems, getShareGroupItems, SHARE_GROUP_FEATURES } from '../entities/nav-items-share';
import { createNavigationGroup, NavigationGooglePermissions, NavigationGroup } from '../entities/side-nav-items';
import { SideNavigationConfigService } from './side-navigation-config.service';

@Injectable({
  providedIn: 'root',
})
export class IndividualFinanceSideMenuService {
  private readonly config = inject(SideNavigationConfigService);
  private readonly environment = inject(Environment);

  public getNavigation(): Observable<NavigationGroup[]> {
    return combineLatest({
      googlePermissions: this.config.getGoogleConfig(),
      showCuratedSocial: this.config.showSocialStudioLink$,
      displayBrandKits: this.config.getLiveFeature(TempFeatureFlags.BrandKitAdmin, false),
    }).pipe(
      map(({ googlePermissions, showCuratedSocial, displayBrandKits }) => {
        const { baseUrl } = this.environment;
        const userType: NavigationUserType = 'agent';
        return [
          createNavigationGroup([getProfile()]),
          createNavigationGroup([getOverview()]),
          this.getCollectGroup(userType),
          this.getShareGroup(userType, googlePermissions, baseUrl, showCuratedSocial),
          this.getMonitoringGroup(),
          this.getManageItems(baseUrl, false, displayBrandKits),
        ].filter((group) => group.items.length);
      }),
    );
  }

  private getCollectGroup(userType: NavigationUserType) {
    const notAvailable: CollectGroupFeature[] = ['leasing', 'nps', 'importReviews', 'properties'];
    const features = COLLECT_GROUP_FEATURES.filter((f) => !notAvailable.includes(f));

    const items = getCollectGroupItems(features, userType);
    return createNavigationGroup(items, 'collect');
  }

  private getShareGroup(
    agentType: NavigationUserType,
    googlePermissions: NavigationGooglePermissions,
    baseUrl: string,
    showStudio: boolean,
  ): NavigationGroup {
    const features = SHARE_GROUP_FEATURES.filter(filterShareItems(false, true, true, showStudio, false));
    const items = getShareGroupItems(features, agentType, false, googlePermissions, baseUrl);
    return createNavigationGroup(items, 'share');
  }

  private getManageItems(baseUrl: string, enableDatafeeds: boolean, displayBrandKits: boolean): NavigationGroup {
    return createNavigationGroup(
      [
        ...getManageGroupItems(this.getManageGroupItems()),
        getSettingsGroupItems(this.getSettingsItems(enableDatafeeds, displayBrandKits), baseUrl),
      ],
      'manage',
    );
  }

  private getSettingsItems(enableDatafeeds: boolean, displayBrandKits: boolean): SettingGroupFeature[] {
    const notAvailable: SettingGroupFeature[] = ['manageTemplates', 'easyReviews', 'assistants'];

    if (!enableDatafeeds) {
      notAvailable.push('dataFeeds');
    }

    if (!displayBrandKits) {
      notAvailable.push('brandKits');
    }

    const items = SETTINGS_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));

    return items;
  }

  private getManageGroupItems() {
    const notAvailable: ManageGroupFeature[] = ['teamAndAdmins'];
    const items = MANAGE_GROUP_FEATURES.filter((i) => !notAvailable.includes(i));

    return items;
  }

  private getMonitoringGroup(): NavigationGroup {
    const notAvailble: MonitorGroupFeature[] = ['leaderboards'];
    const features = MONITORING_GROUP_FEATURES.filter((i) => !notAvailble.includes(i));
    return createNavigationGroup(getMonitorGroupItems(features), 'monitor');
  }
}
